import React, { useState } from "react";

import { Loader2 } from "lucide-react";

import { PlanCard } from "./PlanCard";
import {
  apiErrorMessage,
  useAccountPlanActions,
} from "../../Hooks/useAccountPlan";
import type {
  AccountPlanOverview,
  SectionOwner,
} from "../../Types/AccountPlan";

interface Props {
  accountId: string;
  data: AccountPlanOverview;
  /** Only the AE changes owners (backend 403s everyone else). */
  canEdit: boolean;
  onChanged: () => void;
}

const ROLES: SectionOwner[] = ["AE", "CSM"];

const OWNER_CHIP: Record<SectionOwner, string> = {
  AE: "border-purple-200 bg-purple-50 text-[#8538d4]",
  CSM: "border-teal-200 bg-teal-50 text-teal-700",
};

const PlanSectionOwnersCard: React.FC<Props> = ({
  accountId,
  data,
  canEdit,
  onChanged,
}) => {
  const { setSectionOwner } = useAccountPlanActions();
  const [saving, setSaving] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const owners = data.settings.section_owners;
  const changed = data.settings.owners_changed;

  const change = async (id: string, title: string, role: SectionOwner) => {
    setSaving(id);
    setError(null);
    setStatus(null);
    try {
      const res = await setSectionOwner(accountId, id, role);
      setStatus(
        `${title} → ${role}. ${role} now verifies this section before publish.${
          res.verification_cleared
            ? " The previous verification was cleared."
            : ""
        }`
      );
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't change that section's owner."));
    } finally {
      setSaving(null);
    }
  };

  return (
    <PlanCard
      title="Section owners"
      subtitle={`Who verifies each briefing section before it's published. Defaults set by your program admin${
        canEdit
          ? " — you can change them for this account."
          : " · the AE can change them."
      }`}
      aside={
        changed.length ? (
          <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-800">
            {changed.length} changed
          </span>
        ) : (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
            defaults
          </span>
        )
      }
    >
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {data.sections
          .filter(s => s.enabled !== false)
          .map(s => {
            const owner = owners[s.id] || null;
            const isChanged = changed.indexOf(s.id) >= 0;
            const num = s.id.replace(/^s/, "");
            const selectId = `section-owner-${s.id}`;
            return (
              <div
                key={s.id}
                className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 ${
                  isChanged
                    ? "border-purple-200 bg-purple-50/40"
                    : "border-gray-200"
                }`}
              >
                <label
                  htmlFor={
                    canEdit && s.id !== "s1" && owner ? selectId : undefined
                  }
                  className="min-w-0 text-xs text-gray-700"
                >
                  <span className="font-mono text-gray-400">{num}</span>{" "}
                  {s.title}
                </label>
                {s.id === "s1" || !owner ? (
                  <span className="shrink-0 text-right text-[11px] text-gray-400">
                    {s.id === "s1" ? "System facts · no owner" : "No owner"}
                  </span>
                ) : canEdit ? (
                  <span className="flex shrink-0 items-center gap-1">
                    {saving === s.id && (
                      <Loader2
                        className="h-3 w-3 animate-spin text-[#974ae4]"
                        aria-hidden
                      />
                    )}
                    <select
                      id={selectId}
                      value={owner}
                      disabled={saving !== null}
                      onChange={e =>
                        void change(
                          s.id,
                          s.title,
                          e.target.value as SectionOwner
                        )
                      }
                      className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:opacity-60"
                    >
                      {ROLES.map(r => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </span>
                ) : (
                  <span
                    className={`shrink-0 rounded border px-1.5 py-0.5 font-mono text-[10px] font-semibold ${OWNER_CHIP[owner]}`}
                  >
                    {owner}
                  </span>
                )}
              </div>
            );
          })}
      </div>
      {status && (
        <p role="status" className="mt-3 text-xs text-emerald-700">
          {status}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-3 text-xs text-red-600">
          {error}
        </p>
      )}
    </PlanCard>
  );
};

export default PlanSectionOwnersCard;
